import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Plus } from "lucide-react";
import { toast } from "sonner";
import { useAdminAuth } from "@/context/AdminAuthContext";
import { useSchemas } from "@/context/SchemasContext";
import { ProductPhotoGallery } from "./ProductPhotoGallery";
import { VarietyMultiSelect } from "./VarietyMultiSelect";

const slugify = (s) =>
  s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const empty = {
  slug: "",
  title: "",
  category: "",
  price: "",
  mrp: "",
  monogram_price: "",
  caption: "",
  description: "",
  material: "",
  care: "",
  tag: "",
  stock: 100,
  sort_order: 100,
  featured: false,
  sizes: [],
  size_inches: [],
  size_addons: [], // [{name, price}]
  size_prices: {}, // { "Twin": 105, "Queen": 120, "King": 135 } — absolute per-size prices, overrides base price
  colours: [],
  fabric: [],
  images: [],
  images_originals: [],
};

// Flatten schema suggestion arrays into simple options.
const collectSizeSuggestions = (schema) => {
  const s = schema?.sizes;
  if (!s) return [];
  if (Array.isArray(s.list)) return s.list;
  if (s.regions && typeof s.regions === "object") {
    return Array.from(new Set(Object.values(s.regions).flat()));
  }
  return [];
};

export const NewProductModal = ({ open, onClose, onCreate, onUpdate, categories, editing = null }) => {
  const { token } = useAdminAuth();
  const { getSchema } = useSchemas();
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [autoSlug, setAutoSlug] = useState(true);
  const [createdSlug, setCreatedSlug] = useState(null); // enables gallery after create

  const isEdit = !!editing;
  const effectiveSlug = isEdit ? editing?.slug : createdSlug;

  const activeCats = useMemo(
    () => (categories || []).filter((c) => !c.is_deleted),
    [categories]
  );

  const schema = useMemo(() => getSchema(form.category), [form.category, getSchema]);
  const sizeSuggest = useMemo(() => collectSizeSuggestions(schema), [schema]);
  const colourSuggest = useMemo(() => schema?.colours?.options || [], [schema]);
  const fabricSuggest = useMemo(() => schema?.fabrics?.options || [], [schema]);

  // Guard: only run the "initialise form from editing/empty" branch when the
  // modal transitions from closed→open or when the `editing` reference changes.
  // Parent-triggered category refetches must NOT wipe the local form state,
  // otherwise the inline photo gallery unlock (createdSlug) is lost.
  const initKeyRef = useRef({ open: false, editingSlug: null });
  useEffect(() => {
    const editingSlug = editing?.slug || null;
    const shouldReset =
      open !== initKeyRef.current.open ||
      editingSlug !== initKeyRef.current.editingSlug;
    if (!shouldReset) return;
    initKeyRef.current = { open, editingSlug };
    if (!open) return;

    if (editing) {
      const src = editing.custom || editing.override || {};
      setForm({
        slug: editing.slug || "",
        title: editing.title || "",
        category: editing.category || activeCats[0]?.slug || "",
        price: String(editing.price ?? ""),
        mrp: String(src.mrp ?? editing.mrp ?? ""),
        monogram_price: String(src.monogram_price ?? editing.monogram_price ?? ""),
        caption: src.caption ?? "",
        description: src.description ?? "",
        material: src.material ?? "",
        care: src.care ?? "",
        tag: src.tag ?? "",
        stock: editing.stock ?? 100,
        sort_order: editing.sort_order ?? 100,
        featured: !!editing.featured,
        sizes: Array.isArray(src.sizes) ? src.sizes : [],
        size_inches: Array.isArray(src.size_inches) ? src.size_inches : [],
        size_addons: Array.isArray(src.size_addons) ? src.size_addons : [],
        size_prices: (src.size_prices && typeof src.size_prices === "object") ? src.size_prices : {},
        colours: Array.isArray(src.colours) ? src.colours : [],
        fabric: Array.isArray(src.fabric) ? src.fabric : [],
        images: Array.isArray(src.images) ? src.images : (editing.hero_image ? [editing.hero_image] : []),
        images_originals: Array.isArray(src.images_originals) ? src.images_originals : [],
      });
      setAutoSlug(false);
      setCreatedSlug(editing.slug);
    } else {
      setForm({ ...empty, category: activeCats[0]?.slug || "" });
      setAutoSlug(true);
      setCreatedSlug(null);
    }
    setSaving(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editing]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onTitle = (v) => {
    setForm((f) => ({ ...f, title: v, slug: autoSlug ? slugify(v) : f.slug }));
  };

  const submit = async (e) => {
    e?.preventDefault();
    if (saving) return;
    if (!form.title.trim()) return toast.error("Title is required");
    if (!form.slug.trim()) return toast.error("Slug is required");
    if (!form.category) return toast.error("Pick a category");
    const price = Number(form.price);
    if (!price || price <= 0) return toast.error("Enter a valid price");

    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        category: form.category,
        price,
        mrp: Number(form.mrp) || undefined,
        monogram_price: Number(form.monogram_price) || 0,
        caption: form.caption.trim(),
        description: form.description.trim(),
        material: form.material.trim(),
        care: form.care.trim(),
        tag: form.tag.trim(),
        stock: Number(form.stock) || 0,
        sort_order: Number(form.sort_order) || 100,
        featured: !!form.featured,
        sizes: form.sizes.length ? form.sizes : undefined,
        size_inches: form.size_inches.length ? form.size_inches : undefined,
        size_addons: form.size_addons.length ? form.size_addons : undefined,
        size_prices: (form.size_prices && Object.keys(form.size_prices).length) ? form.size_prices : undefined,
        colours: form.colours.length ? form.colours : undefined,
        fabric: form.fabric.length ? form.fabric : undefined,
      };
      if (isEdit) {
        await onUpdate(form.slug.trim(), payload);
        toast.success("Product updated");
      } else {
        await onCreate({ slug: form.slug.trim(), ...payload });
        setCreatedSlug(form.slug.trim());
        toast.success("Product created — now add photos below");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] bg-forest/60 backdrop-blur-sm flex items-center justify-center p-4 md:p-8"
          onClick={onClose}
          data-testid="new-product-modal"
        >
          <motion.form
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.7, 0, 0.2, 1] }}
            onSubmit={submit}
            onClick={(e) => e.stopPropagation()}
            className="bg-white w-full max-w-4xl max-h-[92vh] overflow-y-auto border border-hairline"
          >
            <header className="sticky top-0 bg-white border-b border-hairline px-6 md:px-8 py-5 flex items-center justify-between z-10">
              <div>
                <div className="overline text-champagne">§ {isEdit ? "Edit product" : "New product"}</div>
                <h2 className="font-display text-forest text-2xl md:text-3xl leading-none mt-1">
                  {isEdit ? "Refine the" : "Add to the"} <span className="font-editorial italic text-champagne">house.</span>
                </h2>
              </div>
              <button
                type="button"
                onClick={onClose}
                data-testid="close-new-product"
                className="p-2 -mr-2 text-forest/60 hover:text-forest"
                aria-label="Close"
              >
                <X size={18} strokeWidth={1.5} />
              </button>
            </header>

            <div className="p-6 md:p-8 grid grid-cols-12 gap-5">
              <FieldRow label="Title" span={12}>
                <input
                  data-testid="np-title-input"
                  value={form.title}
                  onChange={(e) => onTitle(e.target.value)}
                  className={inputCls}
                  placeholder="Ivoire Sateen Sheet Set"
                />
              </FieldRow>

              <FieldRow label="URL slug" span={7}>
                <input
                  data-testid="np-slug-input"
                  value={form.slug}
                  disabled={isEdit}
                  onChange={(e) => { setAutoSlug(false); set("slug", slugify(e.target.value)); }}
                  className={`${inputCls} font-mono text-sm ${isEdit ? "opacity-50 cursor-not-allowed" : ""}`}
                  placeholder="ivoire-sateen-sheet-set"
                />
              </FieldRow>

              <FieldRow label="Category" span={5}>
                <select
                  data-testid="np-category-input"
                  value={form.category}
                  onChange={(e) => set("category", e.target.value)}
                  className={inputCls}
                >
                  {activeCats.map((c) => (
                    <option key={c.slug} value={c.slug}>{c.label}</option>
                  ))}
                </select>
              </FieldRow>

              <FieldRow label="Price (INR)" span={4}>
                <input
                  data-testid="np-price-input"
                  type="number" min="1"
                  value={form.price}
                  onChange={(e) => set("price", e.target.value)}
                  className={`${inputCls} tabnum`}
                  placeholder="4299"
                />
              </FieldRow>

              <FieldRow label="MRP (INR)" span={4}>
                <input
                  type="number" min="0"
                  value={form.mrp}
                  onChange={(e) => set("mrp", e.target.value)}
                  className={`${inputCls} tabnum`}
                  placeholder="5900"
                />
              </FieldRow>

              <FieldRow label="Stock" span={4}>
                <input
                  type="number" min="0"
                  value={form.stock}
                  onChange={(e) => set("stock", e.target.value)}
                  className={`${inputCls} tabnum`}
                />
              </FieldRow>

              <FieldRow label="Monogram add-on price (INR — 0 = free)" span={6}>
                <input
                  data-testid="np-monogram-price-input"
                  type="number" min="0"
                  value={form.monogram_price}
                  onChange={(e) => set("monogram_price", e.target.value)}
                  className={`${inputCls} tabnum`}
                  placeholder="0 or 750"
                />
              </FieldRow>

              <FieldRow label="Tag" span={6}>
                <input
                  value={form.tag}
                  onChange={(e) => set("tag", e.target.value)}
                  className={inputCls}
                  placeholder="New / Bestseller"
                />
              </FieldRow>

              <FieldRow label="Caption" span={12}>
                <input
                  value={form.caption}
                  onChange={(e) => set("caption", e.target.value)}
                  className={inputCls}
                  placeholder="400 TC Cotton Sateen"
                />
              </FieldRow>

              <FieldRow label="Description" span={12}>
                <textarea
                  value={form.description}
                  onChange={(e) => set("description", e.target.value)}
                  className={`${inputCls} min-h-[100px] resize-y`}
                  placeholder="A quiet homage to bamboo groves — 400 TC long-staple sateen…"
                />
              </FieldRow>

              <FieldRow label="Material" span={6}>
                <input
                  value={form.material}
                  onChange={(e) => set("material", e.target.value)}
                  className={inputCls}
                  placeholder="100% Cotton, 400 TC Sateen"
                />
              </FieldRow>

              <FieldRow label="Care" span={6}>
                <input
                  value={form.care}
                  onChange={(e) => set("care", e.target.value)}
                  className={inputCls}
                  placeholder="Machine wash cold · Tumble low"
                />
              </FieldRow>

              {/* Multi-select variety pickers */}
              <div className="col-span-12 border-t border-hairline pt-5 mt-2">
                <div className="overline text-champagne mb-3">§ Varieties</div>
              </div>

              <VarietyMultiSelect
                label="Sizes"
                testid="np-sizes"
                value={form.sizes}
                suggestions={sizeSuggest}
                onChange={(v) => set("sizes", v)}
                placeholder='e.g., Queen, King, 96" × 108"'
              />

              <VarietyMultiSelect
                label='Sizes in inches (buyer-facing dimensions)'
                testid="np-sizes-inches"
                value={form.size_inches}
                suggestions={['90" × 108"', '104" × 90"', '108" × 96"', '60" × 80"', '72" × 84"']}
                onChange={(v) => set("size_inches", v)}
                placeholder='e.g., 90" × 108"'
              />

              {/* Extra cost per size — auto-syncs with the sizes + inches lists above */}
              <div className="col-span-12" data-testid="np-size-addons">
                <div className="overline text-forest/60 mb-1.5 flex items-center justify-between">
                  <span>Extra cost per size (₹, optional)</span>
                  <span className="text-forest/40 text-[10px] italic normal-case tracking-normal">Auto-synced with sizes above</span>
                </div>
                {(() => {
                  const merged = [];
                  const seen = new Set();
                  [...(form.sizes || []), ...(form.size_inches || [])].forEach((s) => {
                    const key = String(s || "").trim();
                    if (!key) return;
                    const k = key.toLowerCase();
                    if (seen.has(k)) return;
                    seen.add(k);
                    merged.push(key);
                  });
                  if (merged.length === 0) {
                    return (
                      <p className="text-forest/40 text-xs italic">Add sizes above first — each will appear here with a "+₹" field.</p>
                    );
                  }
                  const priceFor = (name) => {
                    const hit = (form.size_addons || []).find(
                      (a) => (a?.name || "").trim().toLowerCase() === name.toLowerCase()
                    );
                    return Number(hit?.price || 0);
                  };
                  const setPriceFor = (name, price) => {
                    const list = [...(form.size_addons || [])];
                    const idx = list.findIndex(
                      (a) => (a?.name || "").trim().toLowerCase() === name.toLowerCase()
                    );
                    if (idx >= 0) list[idx] = { name, price };
                    else list.push({ name, price });
                    // drop stale rows whose size no longer exists
                    const validSet = new Set(merged.map((m) => m.toLowerCase()));
                    const cleaned = list.filter((r) => validSet.has((r?.name || "").trim().toLowerCase()));
                    set("size_addons", cleaned);
                  };
                  return (
                    <div className="space-y-2">
                      {merged.map((s, i) => (
                        <div key={s} className="flex items-center gap-2" data-testid={`np-addon-row-${i}`}>
                          <div className={`${inputCls} flex-1 !cursor-default select-none`} data-testid={`np-addon-name-${i}`}>{s}</div>
                          <span className="text-forest/50 text-sm tabnum">+ ₹</span>
                          <input
                            type="number"
                            min="0"
                            value={priceFor(s)}
                            onChange={(e) => setPriceFor(s, Number(e.target.value) || 0)}
                            placeholder="0"
                            className={`${inputCls} tabnum w-28`}
                            data-testid={`np-addon-price-${i}`}
                          />
                        </div>
                      ))}
                      <p className="text-forest/40 text-[11px] italic">Tip: leave "0" if a size doesn't cost extra. Buyers see the "+₹" beside each size on the product page.</p>
                    </div>
                  );
                })()}
              </div>

              {/* Absolute per-size prices (Twin/Queen/King) — takes precedence over base price + addons */}
              <div className="col-span-12 mt-2" data-testid="np-size-prices-block">
                <div className="overline text-forest/60 mb-1.5 flex items-center justify-between">
                  <span>Absolute price per size (₹, optional)</span>
                  <span className="text-forest/40 text-[10px] italic normal-case tracking-normal">Overrides base price for the selected size</span>
                </div>
                {(() => {
                  const merged = [];
                  const seen = new Set();
                  [...(form.sizes || []), ...(form.size_inches || [])].forEach((s) => {
                    const key = String(s || "").trim();
                    if (!key) return;
                    const k = key.toLowerCase();
                    if (seen.has(k)) return;
                    seen.add(k);
                    merged.push(key);
                  });
                  if (merged.length === 0) {
                    return (
                      <p className="text-forest/40 text-xs italic">Add sizes above first — each will appear here with an absolute "₹" price.</p>
                    );
                  }
                  const priceFor = (name) => Number((form.size_prices || {})[name] || 0);
                  const setPriceFor = (name, price) => {
                    const next = { ...(form.size_prices || {}) };
                    if (price > 0) next[name] = price;
                    else delete next[name];
                    // drop stale keys whose size no longer exists
                    const validSet = new Set(merged.map((m) => m.toLowerCase()));
                    for (const k of Object.keys(next)) {
                      if (!validSet.has(k.toLowerCase())) delete next[k];
                    }
                    set("size_prices", next);
                  };
                  return (
                    <div className="space-y-2">
                      {merged.map((s, i) => (
                        <div key={s} className="flex items-center gap-2" data-testid={`np-sizeprice-row-${i}`}>
                          <div className={`${inputCls} flex-1 !cursor-default select-none`} data-testid={`np-sizeprice-name-${i}`}>{s}</div>
                          <span className="text-forest/50 text-sm tabnum">₹</span>
                          <input
                            type="number"
                            min="0"
                            value={priceFor(s)}
                            onChange={(e) => setPriceFor(s, Number(e.target.value) || 0)}
                            placeholder="0 = use base price"
                            className={`${inputCls} tabnum w-40`}
                            data-testid={`np-sizeprice-value-${i}`}
                          />
                        </div>
                      ))}
                      <p className="text-forest/40 text-[11px] italic">Set to <span className="tabnum">0</span> to fall back to the base product price. The size-specific price flows through to cart, checkout, PayPal and the order record.</p>
                    </div>
                  );
                })()}
              </div>

              <VarietyMultiSelect
                label="Colours"
                testid="np-colours"
                kind="colour"
                value={form.colours}
                suggestions={colourSuggest}
                onChange={(v) => set("colours", v)}
                placeholder="e.g., Ivoire, Forest"
              />

              {/* Per-colour image URL — when the buyer picks this colour on the PDP,
                  the main product photo will swap to this image. */}
              {form.colours.length > 0 && (
                <div className="col-span-12" data-testid="np-colour-images">
                  <div className="overline text-forest/60 mb-1.5">Colour → product photo (optional)</div>
                  <p className="text-forest/50 text-xs mb-3 italic">Paste an image URL for each colour. When the buyer clicks the swatch, the main gallery photo will swap to this shot. Upload the image once via the Photos section, right-click the thumbnail → copy image address.</p>
                  <div className="space-y-2">
                    {form.colours.map((c, i) => (
                      <div key={c.name || i} className="flex items-center gap-2" data-testid={`np-colour-image-row-${i}`}>
                        <span
                          className="h-8 w-8 rounded-full border border-hairline shrink-0"
                          style={{ background: c.hex || "#eee" }}
                          title={c.name}
                        />
                        <div className={`${inputCls} w-40 !cursor-default select-none truncate`}>{c.name}</div>
                        <input
                          value={c.image || ""}
                          onChange={(e) => {
                            const next = [...form.colours];
                            next[i] = { ...c, image: e.target.value };
                            set("colours", next);
                          }}
                          placeholder="/api/files/anabiya-decor/products/…/photo.png"
                          className={`${inputCls} flex-1`}
                          data-testid={`np-colour-image-${i}`}
                        />
                        {c.image && (
                          <img src={c.image.startsWith("http") ? c.image : `${process.env.REACT_APP_BACKEND_URL}${c.image}`} alt="" className="h-10 w-10 object-cover rounded border border-hairline" />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <VarietyMultiSelect
                label="Fabrics"
                testid="np-fabrics"
                value={form.fabric}
                suggestions={fabricSuggest}
                onChange={(v) => set("fabric", v)}
                placeholder="e.g., Sateen, Percale, Linen"
              />

              <FieldRow label="Sort order" span={4}>
                <input
                  type="number" min="0"
                  value={form.sort_order}
                  onChange={(e) => set("sort_order", e.target.value)}
                  className={`${inputCls} tabnum`}
                />
              </FieldRow>

              <div className="col-span-8 flex items-end pb-1">
                <label className="inline-flex items-center gap-2 cursor-pointer text-forest">
                  <input
                    type="checkbox"
                    data-testid="np-featured-input"
                    checked={form.featured}
                    onChange={(e) => set("featured", e.target.checked)}
                    className="accent-forest w-4 h-4"
                  />
                  <span className="overline">Featured on homepage</span>
                </label>
              </div>

              {/* Photo gallery — active once product exists (edit mode or after create) */}
              {effectiveSlug ? (
                <div className="col-span-12 border-t border-hairline pt-5 mt-2">
                  <div className="overline text-champagne mb-3">§ Photos</div>
                  <ProductPhotoGallery
                    slug={effectiveSlug}
                    images={form.images}
                    imagesOriginals={form.images_originals || []}
                    token={token}
                    onChange={(imgs, origs) => {
                      set("images", imgs);
                      if (origs !== undefined) set("images_originals", origs);
                    }}
                  />
                </div>
              ) : (
                <div className="col-span-12 border-t border-hairline pt-5 mt-2">
                  <p className="text-forest/50 text-xs">Create the product first — the photo uploader will unlock here right after.</p>
                </div>
              )}
            </div>

            <footer className="sticky bottom-0 bg-white border-t border-hairline px-6 md:px-8 py-5 flex items-center justify-between gap-3">
              <p className="text-forest/50 text-xs">{effectiveSlug ? "Photos save instantly. Fields save when you click below." : "Fill the details, then create to unlock photo upload."}</p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="overline text-forest border border-hairline rounded-full px-4 py-2 hover:border-forest"
                >
                  Close
                </button>
                <button
                  type="submit"
                  data-testid="np-submit"
                  disabled={saving}
                  className="inline-flex items-center gap-2 overline text-white bg-forest rounded-full px-5 py-2.5 disabled:opacity-40"
                >
                  <Plus size={13} strokeWidth={1.8} /> {saving ? "Saving…" : isEdit ? "Save changes" : (effectiveSlug ? "Update product" : "Create product")}
                </button>
              </div>
            </footer>
          </motion.form>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const inputCls =
  "w-full bg-transparent border border-hairline rounded-lg px-3 py-2.5 text-sm text-forest focus:outline-none focus:border-forest transition-colors";

const spanMap = {
  4: "md:col-span-4",
  5: "md:col-span-5",
  6: "md:col-span-6",
  7: "md:col-span-7",
  8: "md:col-span-8",
  12: "md:col-span-12",
};

const FieldRow = ({ label, children, span = 12 }) => (
  <div className={`col-span-12 ${spanMap[span] || "md:col-span-12"}`}>
    <div className="overline text-forest/60 mb-1.5">{label}</div>
    {children}
  </div>
);
